import { useLocation, useHistory, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import PageTitle from "../../../layouts/PageTitle";
import { Form, Button, Row, Col, Card, Alert, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
    ArrowLeft,
    Save,
    X,
    Copy,
    Calculator,
    Calendar,
    FileText,
    AlertTriangle,
    CheckCircle,
    Plus,
    Trash2,
    Info,
    ArrowRight
} from 'lucide-react';
import "react-datepicker/dist/react-datepicker.css";
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from "../../../../utils/ApiUtils";
import swal from "sweetalert";

import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

// Helper function to format numbers as Swedish currency (SEK)
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
};

const CreateVerificationFromCopy = () => {
    const { companyId } = useParams();
    const location = useLocation();
    const { verificationData, from } = location.state || {};
    const history = useHistory();

    // Initialize state variables
    const [date, setDate] = useState(new Date());
    const [description, setDescription] = useState(verificationData?.description || "");
    const [manualRows, setManualRows] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [balanceStatus, setBalanceStatus] = useState({ message: "", variant: "secondary" });
    const [loading, setLoading] = useState(false);
    const [savingVerification, setSavingVerification] = useState(false);
    const [error, setError] = useState(null);

    // Add the balance checking functions
    const areTransactionsBalanced = () => {
        if (!manualRows || manualRows.length === 0) {
            return false;
        }

        let totalDebit = 0;
        let totalCredit = 0;

        manualRows.forEach((row) => {
            const debitAmount = parseFloat(row.debit || 0);
            const creditAmount = parseFloat(row.credit || 0);

            totalDebit += debitAmount;
            totalCredit += creditAmount;
        });

        // Allow for small rounding differences (within 1 öre)
        const difference = Math.abs(totalDebit - totalCredit);
        return difference < 0.01;
    };

    // Enhanced form validation function that includes balance check
    const isFormValid = () => {
        // Check if description is provided
        if (!description || description.trim() === '') {
            return false;
        }

        // Check if there are transactions
        if (!manualRows || manualRows.length === 0) {
            return false;
        }

        // Check if any transaction is missing required data
        const hasInvalidTransaction = manualRows.some(row => {
            const hasAccount = row.account_id && row.account_number.trim() !== '';
            const hasAmount = (row.debit && parseFloat(row.debit) > 0) ||
                (row.credit && parseFloat(row.credit) > 0);
            return !hasAccount || !hasAmount;
        });

        if (hasInvalidTransaction) {
            return false;
        }

        // Check if transactions are balanced
        return areTransactionsBalanced();
    };

    // Function to get balance status with detailed information
    const getBalanceStatus = () => {
        if (!manualRows || manualRows.length === 0) {
            return {
                isBalanced: false,
                message: "Inga transaktioner",
                variant: "secondary",
                difference: 0
            };
        }

        let totalDebit = 0;
        let totalCredit = 0;

        manualRows.forEach((row) => {
            totalDebit += parseFloat(row.debit || 0);
            totalCredit += parseFloat(row.credit || 0);
        });

        const difference = totalDebit - totalCredit;
        const isBalanced = Math.abs(difference) < 0.01;

        if (isBalanced) {
            return {
                isBalanced: true,
                message: "Debet och Kredit balanserar",
                variant: "success",
                difference: 0,
                totalDebit,
                totalCredit
            };
        } else if (difference > 0) {
            return {
                isBalanced: false,
                message: `Debet är ${formatCurrency(difference)} mer än kredit`,
                variant: "danger",
                difference,
                totalDebit,
                totalCredit
            };
        } else {
            return {
                isBalanced: false,
                message: `Kredit är ${formatCurrency(Math.abs(difference))} mer än debet`,
                variant: "danger",
                difference,
                totalDebit,
                totalCredit
            };
        }
    };

    const fetchAccounts = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await axios.get(getApiUrl(`${companyId}/accounting/accounts`), { withCredentials: true });
            const options = result.data.accounts.map(account => ({
                value: account.account_id,
                label: `${account.account_number} - ${account.account_name}`,
                account_number: account.account_number,
                account_name: account.account_name
            }));
            setAccounts(options);
        } catch (error) {
            console.error('Error fetching accounts:', error);
            setError('Det gick inte att hämta kontolistan. Försök igen senare.');
        } finally {
            setLoading(false);
        }
    };

    // Initialize the component data
    useEffect(() => {
        if (verificationData?.transactions) {
            const rows = verificationData.transactions.map((transaction) => ({
                account_id: transaction.account_id,
                account_number: transaction.account_number,
                account_name: transaction.account_name,
                debit: transaction.transaction_type === "debit" ? transaction.amount : "",
                credit: transaction.transaction_type === "credit" ? transaction.amount : "",
            }));
            setManualRows(rows);
        } else {
            // If no transactions, add at least one empty row
            setManualRows([{ account_id: "", account_number: "", account_name: "", debit: "", credit: "" }]);
        }
        fetchAccounts(); // Fetch accounts when component mounts
    }, [verificationData]);

    // Calculate balance whenever rows change
    useEffect(() => {
        calculateBalance();
    }, [manualRows]);

    // Function to calculate and show balance status
    const calculateBalance = () => {
        let totalDebit = 0;
        let totalCredit = 0;

        manualRows.forEach((row) => {
            totalDebit += parseFloat(row.debit || 0);
            totalCredit += parseFloat(row.credit || 0);
        });

        const diff = Math.abs(totalDebit - totalCredit);

        if (diff < 0.01) { // Allow for rounding errors
            setBalanceStatus({
                message: "Debet och Kredit balanserar",
                variant: "success"
            });
        } else if (totalDebit > totalCredit) {
            setBalanceStatus({
                message: `Debet är mer än kredit med ${formatCurrency(totalDebit - totalCredit)}`,
                variant: "danger"
            });
        } else if (totalCredit > totalDebit) {
            setBalanceStatus({
                message: `Kredit är mer än debet med ${formatCurrency(totalCredit - totalDebit)}`,
                variant: "danger"
            });
        } else {
            setBalanceStatus({
                message: "",
                variant: "secondary"
            });
        }
    };



    const handleAddRow = () => {
        setManualRows([...manualRows, { account_id: "", account_number: "", account_name: "", debit: "", credit: "" }]);
    };

    const handleRemoveRow = (index) => {
        if (manualRows.length > 1) {
            setManualRows(manualRows.filter((row, rowIndex) => rowIndex !== index));
        }
    };

    const handleRowChange = (index, field, value) => {
        const newRows = [...manualRows];
        if (field === "account_id") {
            const selectedAccount = accounts.find((option) => option.value === value);
            if (selectedAccount) {
                newRows[index] = {
                    ...newRows[index],
                    account_id: selectedAccount.value,
                    account_number: selectedAccount.account_number,
                    account_name: selectedAccount.account_name,
                };
            }
        } else if (field === "debit") {
            // If debit is entered, clear credit
            newRows[index] = {
                ...newRows[index],
                debit: value,
                credit: value ? "" : newRows[index].credit
            };
        } else if (field === "credit") {
            // If credit is entered, clear debit
            newRows[index] = {
                ...newRows[index],
                credit: value,
                debit: value ? "" : newRows[index].debit
            };
        } else {
            newRows[index][field] = value;
        }
        setManualRows(newRows);
    };

    const handleSave = () => {
        if (!isFormValid()) {
            swal("Fel", "Kontrollera att alla fält är ifyllda och att debet och kredit balanserar", "error");
            return;
        }
        handleAddVerification();
    };

    const handleAbort = () => {
        // Show confirmation if user has made changes
        if (description || manualRows.some(row => row.account_id || row.debit || row.credit)) {
            swal({
                title: "Är du säker?",
                text: "Ändringar du gjort kommer att gå förlorade",
                icon: "warning",
                buttons: ["Avbryt", "Ja, avbryt"],
                dangerMode: true,
            }).then((willDiscard) => {
                if (willDiscard) {
                    navigateBack();
                }
            });
        } else {
            navigateBack();
        }
    };

    const navigateBack = () => {
        // Navigate back to the verification list without a refresh flag
        const basePath = from || `/${companyId}/verifications`;
        history.push(basePath);
    };

    const handleAddVerification = async () => {
        setSavingVerification(true);
        setError(null);

        try {
            const createVerificationApiUrl = getApiUrl(`${companyId}/verifications`);
            const theDate = date.toISOString().split('T')[0];

            // Format transactions
            const transactions = manualRows.map(row => {
                // Determine transaction type based on debit or credit
                const transaction_type = row.debit ? 'debit' : 'credit';
                const amount = row.debit || row.credit;

                return {
                    account_id: row.account_id,
                    transaction_type,
                    amount
                };
            });

            const response = await axios.post(
                createVerificationApiUrl,
                {
                    date: theDate,
                    description,
                    series: 'V',
                    transactions,
                    attachments: []
                },
                { withCredentials: true }
            );

            if (response.data.success) {
                swal("Verifikat skapat", "Verifikatet har skapats och lagts till i listan", "success");

                // Navigate back with a refresh flag
                const basePath = from || `/${companyId}/verifications`;
                const returnPath = basePath.includes('?')
                    ? basePath
                    : `${basePath}?updated=true`;

                history.push(returnPath);
            } else {
                throw new Error(response.data.message || "Det gick inte att skapa verifikatet");
            }
        } catch (error) {
            console.error('Error adding verification:', error);
            setError(error.response?.data?.message || "Det gick inte att skapa verifikatet. Försök igen senare.");
            setSavingVerification(false);
        }
    };

    // Calculate totals for display
    const totalDebit = manualRows.reduce((sum, row) => sum + parseFloat(row.debit || 0), 0);
    const totalCredit = manualRows.reduce((sum, row) => sum + parseFloat(row.credit || 0), 0);
    const isBalanced = Math.abs(totalDebit - totalCredit) <= 0.01;

    // Calculate original verification totals
    // Convert amounts to numbers to ensure proper addition (not string concatenation)
    const originalDebitTotal = verificationData?.transactions?.reduce((sum, t) => {
        const amount = t.transaction_type === 'debit' ? Number(t.amount) || 0 : 0;
        return sum + amount;
    }, 0) || 0;
    
    const originalCreditTotal = verificationData?.transactions?.reduce((sum, t) => {
        const amount = t.transaction_type === 'credit' ? Number(t.amount) || 0 : 0;
        return sum + amount;
    }, 0) || 0;

    // If no verification data provided, show error
    if (!verificationData) {
        return (
            <div className="container-fluid">
                <PageTitle motherMenu="Bokföring" activeMenu={"Verifikationer"} />
                <Alert variant="danger">
                    Ingen verifikationsinformation hittades. Gå tillbaka och försök igen.
                </Alert>
                <Button variant="primary" onClick={navigateBack}>
                    Tillbaka till verifikationer
                </Button>
            </div>
        );
    }

    if (loading && accounts.length === 0) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </Spinner>
                    <p className="mt-2">Laddar kontoplan...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid" style={{ maxWidth: '1400px' }}>
            <style>{`
                .copy-section {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                    border: 1px solid #e2e8f0;
                }
                
                .section-icon {
                    width: 24px;
                    height: 24px;
                    color: #64748b;
                }
                
                .page-header {
                    background: linear-gradient(135deg, #059669 0%, #047857 100%);
                    color: white;
                    border-radius: 12px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                }
                
                .copy-indicator {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 2rem 0;
                    gap: 1rem;
                    flex-wrap: wrap;
                }
                
                .copy-step {
                    background: white;
                    border: 2px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 1rem;
                    text-align: center;
                    min-width: 200px;
                    transition: all 0.2s ease;
                }
                
                .copy-step.original {
                    border-color: #3b82f6;
                    background: #f0f9ff;
                }
                
                .copy-step.new {
                    border-color: #059669;
                    background: #ecfdf5;
                }
                
                .copy-arrow {
                    color: #64748b;
                    font-size: 1.5rem;
                }
                
                .verification-table {
                    background: white;
                    border-radius: 8px;
                    overflow: hidden;
                    border: 1px solid #e2e8f0;
                }
                
                .table-header {
                    background: #f1f5f9;
                    border-bottom: 1px solid #e2e8f0;
                }
                
                .amount-cell {
                    font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
                    font-size: 0.875rem;
                }
                
                .balance-indicator {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1rem;
                    border-radius: 8px;
                    font-weight: 500;
                    font-size: 0.875rem;
                    margin-bottom: 1rem;
                }
                
                .balance-good {
                    background: #dcfce7;
                    color: #166534;
                    border: 1px solid #bbf7d0;
                }
                
                .balance-bad {
                    background: #fef2f2;
                    color: #dc2626;
                    border: 1px solid #fecaca;
                }
                
                .action-button {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #374151;
                    text-decoration: none;
                    transition: all 0.2s ease;
                    font-size: 0.875rem;
                    font-weight: 500;
                    cursor: pointer;
                }
                
                .action-button:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
                
                .action-button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                
                .action-button.primary {
                    background: #059669;
                    color: white;
                    border-color: #059669;
                }
                
                .action-button.primary:hover:not(:disabled) {
                    background: #047857;
                    border-color: #047857;
                    color: white;
                }
                
                .action-button.secondary {
                    background: #6b7280;
                    color: white;
                    border-color: #6b7280;
                }
                
                .action-button.secondary:hover:not(:disabled) {
                    background: #4b5563;
                    border-color: #4b5563;
                    color: white;
                }
                
                .transaction-row {
                    transition: all 0.2s ease;
                }
                
                .transaction-row:hover {
                    background: #f8fafc;
                }
                
                .add-row-button {
                    color: #059669;
                    border: 2px dashed #cbd5e1;
                    background: transparent;
                    padding: 1rem;
                    text-align: center;
                    border-radius: 8px;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                
                .add-row-button:hover {
                    border-color: #059669;
                    background: #f0fdf4;
                }
            `}</style>

            <PageTitle motherMenu="Bokföring" activeMenu={"Verifikationer"} />

            {/* Page Header */}
            <div className="page-header">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <button
                            className="btn btn-link text-white p-0 me-3"
                            onClick={navigateBack}
                            disabled={savingVerification}
                        >
                            <ArrowLeft size={24} />
                        </button>
                        <div>
                            <h4 className="mb-1">Skapa Verifikat från Kopia</h4>
                            <div className="text-white-50">
                                Kopierar från verifikat {verificationData?.series}{verificationData?.number}
                            </div>
                        </div>
                    </div>
                    <Copy size={32} className="text-white-50" />
                </div>
            </div>

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {/* Copy Indicator */}
            <div className="copy-indicator">
                <div className="copy-step original">
                    <div className="d-flex align-items-center justify-content-center mb-2">
                        <FileText size={20} className="text-primary" />
                    </div>
                    <div className="fw-medium text-primary">Ursprungligt</div>
                    <div className="small text-muted">{verificationData?.series}{verificationData?.number}</div>
                </div>

                <ArrowRight className="copy-arrow" size={24} />

                <div className="copy-step new">
                    <div className="d-flex align-items-center justify-content-center mb-2">
                        <Copy size={20} className="text-success" />
                    </div>
                    <div className="fw-medium text-success">Ny Kopia</div>
                    <div className="small text-muted">Redigera och spara</div>
                </div>
            </div>

            <Row>
                {/* Left Column: Create Form */}
                <Col lg={8}>
                    <div className="copy-section">
                        <div className="d-flex align-items-center mb-4">
                            <Copy className="section-icon me-2" />
                            <h5 className="mb-0 fw-medium">Nytt Verifikat</h5>
                        </div>

                        {/* Basic Information */}
                        <Row className="mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-medium">
                                        <Calendar size={16} className="me-2" />
                                        Datum
                                    </Form.Label>
                                    <DatePicker
                                        selected={date}
                                        onChange={setDate}
                                        dateFormat="yyyy-MM-dd"
                                        className="form-control"
                                        disabled={savingVerification}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-medium">
                                        <FileText size={16} className="me-2" />
                                        Beskrivning
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="Ange beskrivning"
                                        disabled={savingVerification}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Balance Status 
                        <div className={`balance-indicator ${isBalanced ? 'balance-good' : 'balance-bad'}`}>
                            {isBalanced ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                            {balanceStatus.message}
                        </div>*/}

                        {/* Transactions Table */}
                        <div className="d-flex align-items-center mb-3">
                            <Calculator className="section-icon me-2" />
                            <h6 className="mb-0 fw-medium">Transaktioner</h6>
                        </div>

                        <div className="verification-table">
                            <div className="table-responsive">
                                <table className="table mb-0">
                                    <thead className="table-header">
                                        <tr>
                                            <th className="border-0 py-3 px-4 fw-medium text-muted">Konto</th>
                                            <th className="border-0 py-3 px-4 fw-medium text-muted text-end">Debet (SEK)</th>
                                            <th className="border-0 py-3 px-4 fw-medium text-muted text-end">Kredit (SEK)</th>
                                            <th className="border-0 py-3 px-4 fw-medium text-muted" style={{ width: '50px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {manualRows.map((row, index) => (
                                            <tr key={index} className="transaction-row">
                                                <td className="py-3 px-4 border-0">
                                                    <Select
                                                        options={accounts}
                                                        value={accounts.find((option) => option.value === row.account_id)}
                                                        onChange={(selectedOption) =>
                                                            handleRowChange(index, "account_id", selectedOption ? selectedOption.value : "")
                                                        }
                                                        isDisabled={savingVerification}
                                                        placeholder="Välj konto..."
                                                        styles={{
                                                            control: (base) => ({
                                                                ...base,
                                                                minWidth: "250px",
                                                                border: '1px solid #e2e8f0',
                                                                borderRadius: '6px'
                                                            })
                                                        }}
                                                    />
                                                </td>
                                                <td className="py-3 px-4 border-0">
                                                    <Form.Control
                                                        type="number"
                                                        value={row.debit}
                                                        onChange={(e) => handleRowChange(index, "debit", e.target.value)}
                                                        placeholder="0.00"
                                                        step="0.01"
                                                        min="0"
                                                        disabled={savingVerification}
                                                        className="amount-cell text-end"
                                                    />
                                                </td>
                                                <td className="py-3 px-4 border-0">
                                                    <Form.Control
                                                        type="number"
                                                        value={row.credit}
                                                        onChange={(e) => handleRowChange(index, "credit", e.target.value)}
                                                        placeholder="0.00"
                                                        step="0.01"
                                                        min="0"
                                                        disabled={savingVerification}
                                                        className="amount-cell text-end"
                                                    />
                                                </td>
                                                <td className="py-3 px-4 border-0 text-center">
                                                    {manualRows.length > 1 && (
                                                        <button
                                                            className="btn btn-link text-danger p-1"
                                                            onClick={() => handleRemoveRow(index)}
                                                            disabled={savingVerification}
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="border-top bg-light">
                                        <tr>
                                            <td className="py-3 px-4 fw-bold">Summa</td>
                                            <td className="py-3 px-4 text-end fw-bold amount-cell text-success">
                                                {formatCurrency(totalDebit)}
                                            </td>
                                            <td className="py-3 px-4 text-end fw-bold amount-cell text-primary">
                                                {formatCurrency(totalCredit)}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        {/* Add Row Button */}
                        <div
                            className="add-row-button mt-3"
                            onClick={handleAddRow}
                            style={{ cursor: savingVerification ? 'not-allowed' : 'pointer' }}
                        >
                            <Plus size={16} className="me-2" />
                            Lägg till transaktion
                        </div>

                        {/* Action Buttons */}
                        <div className="d-flex gap-3 mt-4 pt-3 border-top">
                            <button
                                className={`action-button primary ${!isFormValid() || savingVerification ? 'disabled' : ''}`}
                                onClick={handleSave}
                                disabled={!isFormValid() || savingVerification}
                                title={!areTransactionsBalanced() ? "Transaktioner måste balansera för att spara" : ""}
                            >
                                {savingVerification ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        Skapar verifikat...
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} />
                                        Skapa Verifikat
                                    </>
                                )}
                            </button>

                            <button
                                className="action-button secondary"
                                onClick={handleAbort}
                                disabled={savingVerification}
                            >
                                <X size={16} />
                                Avbryt
                            </button>
                        </div>
                    </div>
                </Col>

                {/* Right Column: Original Verification Reference */}
                <Col lg={4}>
                    <div className="copy-section">
                        <div className="d-flex align-items-center mb-3">
                            <FileText className="section-icon me-2" style={{ color: '#3b82f6' }} />
                            <h6 className="mb-0 fw-medium">Ursprungligt Verifikat</h6>
                            <span className="badge bg-primary ms-2">Referens</span>
                        </div>

                        <div className="small mb-3">
                            <div><strong>Nummer:</strong> {verificationData?.series}{verificationData?.number}</div>
                            <div><strong>Datum:</strong> {new Date(verificationData?.date).toLocaleDateString('sv-SE')}</div>
                            <div><strong>Beskrivning:</strong> {verificationData?.description}</div>
                        </div>

                        <div className="verification-table">
                            <div className="table-responsive">
                                <table className="table table-sm mb-0">
                                    <thead className="table-header">
                                        <tr>
                                            <th className="border-0 py-2 px-3 small">Konto</th>
                                            <th className="border-0 py-2 px-3 small text-end">Debet</th>
                                            <th className="border-0 py-2 px-3 small text-end">Kredit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {verificationData?.transactions?.map((transaction, index) => (
                                            <tr key={index}>
                                                <td className="py-2 px-3 border-0 small">
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip>
                                                                {transaction.account_name}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span>{transaction.account_number}</span>
                                                    </OverlayTrigger>
                                                </td>
                                                <td className="py-2 px-3 border-0 text-end small amount-cell">
                                                    {transaction.transaction_type === 'debit' ?
                                                        formatCurrency(transaction.amount) : '—'}
                                                </td>
                                                <td className="py-2 px-3 border-0 text-end small amount-cell">
                                                    {transaction.transaction_type === 'credit' ?
                                                        formatCurrency(transaction.amount) : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="border-top bg-light">
                                        <tr>
                                            <td className="py-2 px-3 fw-bold small">Summa</td>
                                            <td className="py-2 px-3 text-end fw-bold small amount-cell">
                                                {formatCurrency(originalDebitTotal)}
                                            </td>
                                            <td className="py-2 px-3 text-end fw-bold small amount-cell">
                                                {formatCurrency(originalCreditTotal)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Information Panel */}
                    <div className="copy-section">
                        <div className="d-flex align-items-center mb-3">
                            <Info className="section-icon me-2" />
                            <h6 className="mb-0 fw-medium">Information</h6>
                        </div>

                        <div className="small text-muted">
                            <p>Du skapar en kopia av det ursprungliga verifikatet.</p>
                            <ul className="ps-3 mb-3">
                                <li>Alla transaktioner kopieras automatiskt</li>
                                <li>Du kan redigera transaktioner innan du sparar</li>
                                <li>Det nya verifikatet får ett nytt verifikatsnummer</li>
                                <li>Datumet kan ändras till önskat datum</li>
                            </ul>
                            <p className="mb-0">Det ursprungliga verifikatet påverkas inte.</p>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default CreateVerificationFromCopy;


// import { useLocation, useHistory, useParams } from "react-router-dom";
// import { useState, useEffect } from "react";
// import PageTitle from "../../../layouts/PageTitle";
// import { Form, Button, Row, Col, Card, Alert, Spinner } from "react-bootstrap";
// import DatePicker from "react-datepicker";
// import Select from "react-select";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faTrash, faSpinner } from "@fortawesome/free-solid-svg-icons";
// import "react-datepicker/dist/react-datepicker.css";
// import axios from '../../../../services/AxiosInstance';
// import { getApiUrl } from "../../../../utils/ApiUtils";
// import swal from "sweetalert";

// const CreateVerificationFromCopy = () => {
//     const { companyId } = useParams();
//     const location = useLocation();
//     const { verificationData, from } = location.state || {};
//     const history = useHistory();

//     // Initialize state variables
//     const [date, setDate] = useState(new Date());
//     const [description, setDescription] = useState(verificationData?.description || "");
//     const [manualRows, setManualRows] = useState([]);
//     const [accounts, setAccounts] = useState([]);
//     const [balanceStatus, setBalanceStatus] = useState("");
//     const [loading, setLoading] = useState(false);
//     const [savingVerification, setSavingVerification] = useState(false);
//     const [error, setError] = useState(null);

//     // Determine if the form is valid
//     const isFormValid = () => {
//         if (!description) return false;
//         if (manualRows.length === 0) return false;

//         // Check if any row is missing required data
//         const invalidRow = manualRows.some(row =>
//             !row.account_id || (!row.debit && !row.credit)
//         );
//         if (invalidRow) return false;

//         // Check if debits and credits are balanced
//         let totalDebit = 0;
//         let totalCredit = 0;
//         manualRows.forEach(row => {
//             totalDebit += parseFloat(row.debit || 0);
//             totalCredit += parseFloat(row.credit || 0);
//         });

//         return Math.abs(totalDebit - totalCredit) < 0.01; // Allow for small rounding differences
//     };

//     const fetchAccounts = async () => {
//         setLoading(true);
//         setError(null);
//         try {
//             const result = await axios.get(getApiUrl(`${companyId}/accounting/accounts`), { withCredentials: true });
//             const options = result.data.map(account => ({
//                 value: account.account_id,
//                 label: `${account.account_number} - ${account.account_name}`,
//                 account_number: account.account_number,
//                 account_name: account.account_name
//             }));
//             setAccounts(options);
//         } catch (error) {
//             console.error('Error fetching accounts:', error);
//             setError('Det gick inte att hämta kontolistan. Försök igen senare.');
//         } finally {
//             setLoading(false);
//         }
//     };

//     // Initialize the component data
//     useEffect(() => {
//         if (verificationData?.transactions) {
//             const rows = verificationData.transactions.map((transaction) => ({
//                 account_id: transaction.account_id,
//                 account_number: transaction.account_number,
//                 account_name: transaction.account_name,
//                 debit: transaction.transaction_type === "debit" ? transaction.amount : "",
//                 credit: transaction.transaction_type === "credit" ? transaction.amount : "",
//             }));
//             setManualRows(rows);
//         } else {
//             // If no transactions, add at least one empty row
//             setManualRows([{ account_id: "", account_number: "", account_name: "", debit: "", credit: "" }]);
//         }
//         fetchAccounts(); // Fetch accounts when component mounts
//     }, [verificationData]);

//     // Calculate balance whenever rows change
//     useEffect(() => {
//         calculateBalance();
//     }, [manualRows]);

//     // Function to calculate and show balance status
//     const calculateBalance = () => {
//         let totalDebit = 0;
//         let totalCredit = 0;

//         manualRows.forEach((row) => {
//             totalDebit += parseFloat(row.debit || 0);
//             totalCredit += parseFloat(row.credit || 0);
//         });

//         const diff = Math.abs(totalDebit - totalCredit);

//         if (diff < 0.01) { // Allow for rounding errors
//             setBalanceStatus({
//                 message: "Debet och Kredit balanserar",
//                 variant: "success"
//             });
//         } else if (totalDebit > totalCredit) {
//             setBalanceStatus({
//                 message: `Debet är mer än kredit med ${new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(totalDebit - totalCredit)}`,
//                 variant: "danger"
//             });
//         } else if (totalCredit > totalDebit) {
//             setBalanceStatus({
//                 message: `Kredit är mer än debet med ${new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(totalCredit - totalDebit)}`,
//                 variant: "danger"
//             });
//         } else {
//             setBalanceStatus({
//                 message: "",
//                 variant: "secondary"
//             });
//         }
//     };

//     const handleAddRow = () => {
//         setManualRows([...manualRows, { account_id: "", account_number: "", account_name: "", debit: "", credit: "" }]);
//     };

//     const handleRemoveRow = (index) => {
//         if (manualRows.length > 1) {
//             setManualRows(manualRows.filter((row, rowIndex) => rowIndex !== index));
//         }
//     };

//     const handleRowChange = (index, field, value) => {
//         const newRows = [...manualRows];
//         if (field === "account_id") {
//             const selectedAccount = accounts.find((option) => option.value === value);
//             if (selectedAccount) {
//                 newRows[index] = {
//                     ...newRows[index],
//                     account_id: selectedAccount.value,
//                     account_number: selectedAccount.account_number,
//                     account_name: selectedAccount.account_name,
//                 };
//             }
//         } else if (field === "debit") {
//             // If debit is entered, clear credit
//             newRows[index] = {
//                 ...newRows[index],
//                 debit: value,
//                 credit: value ? "" : newRows[index].credit
//             };
//         } else if (field === "credit") {
//             // If credit is entered, clear debit
//             newRows[index] = {
//                 ...newRows[index],
//                 credit: value,
//                 debit: value ? "" : newRows[index].debit
//             };
//         } else {
//             newRows[index][field] = value;
//         }
//         setManualRows(newRows);
//     };

//     const handleSave = () => {
//         if (!isFormValid()) {
//             swal("Fel", "Kontrollera att alla fält är ifyllda och att debet och kredit balanserar", "error");
//             return;
//         }
//         handleAddVerification();
//     };

//     const handleAbort = () => {
//         // Show confirmation if user has made changes
//         if (description || manualRows.some(row => row.account_id || row.debit || row.credit)) {
//             swal({
//                 title: "Är du säker?",
//                 text: "Ändringar du gjort kommer att gå förlorade",
//                 icon: "warning",
//                 buttons: ["Avbryt", "Ja, avbryt"],
//                 dangerMode: true,
//             }).then((willDiscard) => {
//                 if (willDiscard) {
//                     navigateBack();
//                 }
//             });
//         } else {
//             navigateBack();
//         }
//     };

//     const navigateBack = () => {
//         // Navigate back to the verification list without a refresh flag
//         const basePath = from || `/${companyId}/verifications`;
//         history.push(basePath);
//     };

//     const handleAddVerification = async () => {
//         setSavingVerification(true);
//         setError(null);

//         try {
//             const createVerificationApiUrl = getApiUrl(`${companyId}/verifications`);
//             const theDate = date.toISOString().split('T')[0];

//             // Format transactions
//             const transactions = manualRows.map(row => {
//                 // Determine transaction type based on debit or credit
//                 const transaction_type = row.debit ? 'debit' : 'credit';
//                 const amount = row.debit || row.credit;

//                 return {
//                     account_id: row.account_id,
//                     transaction_type,
//                     amount
//                 };
//             });

//             const response = await axios.post(
//                 createVerificationApiUrl,
//                 {
//                     date: theDate,
//                     description,
//                     series: 'V',
//                     transactions,
//                     attachments: []
//                 },
//                 { withCredentials: true }
//             );

//             if (response.data.success) {
//                 swal("Verifikat skapat", "Verifikatet har skapats och lagts till i listan", "success");

//                 // Navigate back with a refresh flag
//                 const basePath = from || `/${companyId}/verifications`;
//                 const returnPath = basePath.includes('?')
//                     ? basePath
//                     : `${basePath}?updated=true`;

//                 history.push(returnPath);
//             } else {
//                 throw new Error(response.data.message || "Det gick inte att skapa verifikatet");
//             }
//         } catch (error) {
//             console.error('Error adding verification:', error);
//             setError(error.response?.data?.message || "Det gick inte att skapa verifikatet. Försök igen senare.");
//             setSavingVerification(false);
//         }
//     };

//     // If no verification data provided, show error
//     if (!verificationData) {
//         return (
//             <div className="col-12">
//                 <PageTitle motherMenu="Bokföring" activeMenu={"Verifikationer"} />
//                 <Alert variant="danger">
//                     Ingen verifikationsinformation hittades. Gå tillbaka och försök igen.
//                 </Alert>
//                 <Button variant="primary" onClick={navigateBack}>
//                     Tillbaka till verifikationer
//                 </Button>
//             </div>
//         );
//     }

//     return (
//         <>
//             <div className="col-12">
//                 <PageTitle motherMenu="Bokföring" activeMenu={"Verifikationer"} />
//             </div>

//             <h3 className="mb-4">Skapa verifikat från kopia</h3>

//             {error && (
//                 <Alert variant="danger" onClose={() => setError(null)} dismissible>
//                     {error}
//                 </Alert>
//             )}

//             {loading ? (
//                 <div className="text-center py-5">
//                     <Spinner animation="border" role="status">
//                         <span className="visually-hidden">Laddar...</span>
//                     </Spinner>
//                 </div>
//             ) : (
//                 <div className="col-12">
//                     <div className="row">
//                         <div className="col-md-8">
//                             <Card>
//                                 <Card.Body>
//                                     <Form>
//                                         <Form.Group controlId="date">
//                                             <Form.Label>Datum</Form.Label>
//                                             <DatePicker
//                                                 selected={date}
//                                                 onChange={setDate}
//                                                 dateFormat="yyyy-MM-dd"
//                                                 className="form-control"
//                                                 disabled={savingVerification}
//                                             />
//                                         </Form.Group>

//                                         <Form.Group className="mt-3" controlId="description">
//                                             <Form.Label>Titel</Form.Label>
//                                             <Form.Control
//                                                 type="text"
//                                                 value={description}
//                                                 onChange={(e) => setDescription(e.target.value)}
//                                                 placeholder="Fyll i titel"
//                                                 disabled={savingVerification}
//                                             />
//                                         </Form.Group>

//                                         <h5 className="mt-4">Transaktioner</h5>
//                                         {balanceStatus.message && (
//                                             <div className={`alert alert-${balanceStatus.variant} py-2`}>
//                                                 {balanceStatus.message}
//                                             </div>
//                                         )}

//                                         {manualRows.map((row, index) => (
//                                             <Row key={index} className="align-items-center mb-2">
//                                                 <Col md={5}>
//                                                     {index === 0 && <Form.Label>Konto</Form.Label>}
//                                                     <Select
//                                                         options={accounts}
//                                                         isSearchable={true}
//                                                         value={accounts.find((option) => option.value === row.account_id)}
//                                                         onChange={(selectedOption) =>
//                                                             handleRowChange(index, "account_id", selectedOption ? selectedOption.value : "")
//                                                         }
//                                                         isDisabled={savingVerification}
//                                                         placeholder="Välj konto..."
//                                                     />
//                                                 </Col>
//                                                 <Col>
//                                                     {index === 0 && <Form.Label>Debet</Form.Label>}
//                                                     <Form.Control
//                                                         type="number"
//                                                         value={row.debit}
//                                                         onChange={(e) => handleRowChange(index, "debit", e.target.value)}
//                                                         placeholder="Debet"
//                                                         step="0.01"
//                                                         min="0"
//                                                         disabled={savingVerification}
//                                                     />
//                                                 </Col>
//                                                 <Col>
//                                                     {index === 0 && <Form.Label>Kredit</Form.Label>}
//                                                     <Form.Control
//                                                         type="number"
//                                                         value={row.credit}
//                                                         onChange={(e) => handleRowChange(index, "credit", e.target.value)}
//                                                         placeholder="Kredit"
//                                                         step="0.01"
//                                                         min="0"
//                                                         disabled={savingVerification}
//                                                     />
//                                                 </Col>
//                                                 <Col xs="auto">
//                                                     {index >= 0 && (
//                                                         <Button
//                                                             variant="link"
//                                                             onClick={() => handleRemoveRow(index)}
//                                                             disabled={manualRows.length <= 1 || savingVerification}
//                                                         >
//                                                             <FontAwesomeIcon icon={faTrash} />
//                                                         </Button>
//                                                     )}
//                                                 </Col>
//                                             </Row>
//                                         ))}

//                                         <Button
//                                             className="mt-3"
//                                             variant="link"
//                                             onClick={handleAddRow}
//                                             disabled={savingVerification}
//                                         >
//                                             Lägg till rad
//                                         </Button>

//                                         <div className="mt-4 d-flex gap-2">
//                                             <Button
//                                                 onClick={handleSave}
//                                                 disabled={savingVerification || !isFormValid()}
//                                             >
//                                                 {savingVerification ? (
//                                                     <>
//                                                         <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
//                                                         Sparar...
//                                                     </>
//                                                 ) : "Spara"}
//                                             </Button>
//                                             <Button
//                                                 variant="outline-secondary"
//                                                 onClick={handleAbort}
//                                                 disabled={savingVerification}
//                                             >
//                                                 Avbryt
//                                             </Button>
//                                         </div>
//                                     </Form>
//                                 </Card.Body>
//                             </Card>
//                         </div>
//                         <div className="col-md-4">
//                             <Card>
//                                 <Card.Header>
//                                     <h5 className="mb-0">Ursprungligt verifikat</h5>
//                                 </Card.Header>
//                                 <Card.Body>
//                                     <p><strong>Verifikatnr:</strong> {verificationData.series}{verificationData.number}</p>
//                                     <p><strong>Datum:</strong> {new Date(verificationData.date).toLocaleDateString('sv-SE')}</p>
//                                     <p><strong>Beskrivning:</strong> {verificationData.description}</p>
//                                 </Card.Body>
//                             </Card>
//                         </div>
//                     </div>
//                 </div>
//             )}
//         </>
//     );
// };

// export default CreateVerificationFromCopy;